import { AdminImpersonationDetails } from '../admin-impersonation';
import { Ability, Role } from '../admin-role';
import { BaseIdentifiers } from '../common';
import { FeatureFlags } from '../feature-flags';
import { NotificationName } from '../notification';
import { AssetClass } from '../program/program';
import { UserSecurityCheckStatus } from '../security-check/security-check';
import { UserTradingSystemCredentials } from '../trading-system';
import { VerificationData } from '../user-verification/user-verification';

export const NOT_AVAILABLE = 'N/A';
export const HIDEABLE = 'XXX';

export enum CognitoProviders {
  Password = 'Password',
  Google = 'Google',
}

export interface CognitoIdentity {
  dateCreated: number;
  issuer: string | null;
  primary: boolean;
  providerName: string;
  providerType: string;
  userId: string;
}

export interface CognitoUser {
  userId: string;
  userName: string;
  firstName: string;
  lastName: string;
  identityProviderLastName: string;
  email: string;
  isVerified: boolean;
  identities?: CognitoIdentity[] | null;
}

export interface DescopeUser {
  descopeUserId: string;
  firstName: string;
  lastName: string;
  email: string;
  country?: string;
}

export type AdminUser = CognitoUser & {
  abilities: Ability[];
  role: Role;
  profileUrl: string;
  affiliateId?: string;
};

export interface UserInformation {
  userId: string;
  firstName: string;
  lastName: string;
  userName?: string;
  birthDate?: string; // YYYY-MM-DD format
  email: string;
  phoneNumber: string;
  countryCode?: string;
  zipCode: string;
  country: string;
  address: string;
  city: string;
  state: string;
  profileUrl?: string;
  profile?: UserProfile;
  id?: string;
  securityCheck?: UserSecurityCheck;
  publicId?: string;
  applicationLanguage?: string;
}

export interface UserPaymentData {
  madeFirstPayment?: boolean;

  rise?: {
    riseId?: string;
    invitedAt?: Date;
  };
  interviewInvitationSentAt?: Date;
}

export interface UserChannel {
  type: NotificationName;
  channel: string;
}

export interface UserProfile {
  private?: boolean;
  termsAndConditions?: UserTermsAndConditions;
  doNotSellOrShare?: boolean;
  emailMarketingConsent?: boolean;
}

export interface SecurityCheckModification {
  date: Date;
  admin: {
    id: string;
    name: string;
  };
}

export interface UserSecurityCheck {
  status: UserSecurityCheckStatus;
  lastModified: SecurityCheckModification[];
}

export interface UserContestPreference {
  pinnedPublicIds: string[];
  lastUpdated: Date;
}

export interface UserContestsPreferences {
  [contestId: string]: UserContestPreference;
}

export interface UserAssetPreference {
  selectedAsset?: AssetClass[];
  completedAt?: Date;
}

export type User = BaseIdentifiers &
  UserInformation &
  (CognitoUser | DescopeUser) & {
    featureFlags?: FeatureFlags;
    tsUsers: UserTradingSystemCredentials[];
    confirmationCode?: string;
    resetPasswordCode?: string;
    creditBalance: number;
    verificationData: VerificationData;
    paymentData?: UserPaymentData;
    affiliateId?: string;
    deleted?: boolean;
    channels?: UserChannel[];
    publicId?: string;
    impersonatedDetails?: AdminImpersonationDetails;
    approvals?: Partial<Record<UserApproval, Date>>;
    securityCheck?: UserSecurityCheck;
    contestsPreferences?: UserContestsPreferences;
    assetPreference?: UserAssetPreference;
    marketingConsent?: boolean;
    isAnonymous?: boolean;
  };

export type UserNotificationStatus = {
  channels: UserChannel[];
  unread: number;
};

export interface UserPublicProfile {
  isOwner: boolean;
  firstName: string;
  lastName: string;
  userName?: string;
  profileImg?: string;
  country?: string;
  joinedDate: Date | string;
  verification: {
    isVerified: boolean;
    verificationDate: Date | string;
  };
  publicId?: string;
  isPrivateProfile?: boolean;
}

export enum UserApproval {
  PublicProfile = 'public-profile',
}

export interface UserApprovedRequest {
  approvalType: UserApproval;
}

export interface UserTermsAndConditions {
  acceptTerms: boolean;
  acceptedAt: Date | string;
  acceptedVersion?: string;
}
